import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { strings } from '@/shared/strings';
import { CloseIcon } from '../icons';
import { useShellStore } from './shellStore';
import styles from './Drawer.module.css';

export interface DrawerProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * The right hand drawer, used by Orders.
 *
 * Its open state is registered with the shell store so the scrim is derived
 * from it in the same place as the sidebar's. Two panels each toggling their
 * own scrim is what produced the dead overlay bug in section 12.
 */
export function Drawer({ open, title, onClose, children }: DrawerProps) {
  const titleId = useId();
  const openDrawer = useShellStore((state) => state.openDrawer);
  const closeDrawer = useShellStore((state) => state.closeDrawer);
  const drawerOpen = useShellStore((state) => state.drawerOpen);

  // The Escape handler reads the current callback through a ref so the effect
  // does not re-run on every render and steal focus back, as the modal did.
  const close = useRef(onClose);
  close.current = onClose;

  /*
   * The store follows the prop, and the drawer takes its entry out of the store
   * when it goes.
   *
   * Without the cleanup, a drawer that unmounts while open -- which is what
   * happens whenever the route leaves the screen holding it -- leaves
   * `drawerOpen` true with no drawer on screen, and the scrim sits over the
   * whole app with nothing behind it. That is the reported "the overlay does
   * not close": the panel had already gone.
   */
  useEffect(() => {
    if (open) openDrawer();
    else closeDrawer();
    return () => closeDrawer();
  }, [open, openDrawer, closeDrawer]);

  /**
   * Tapping the scrim closes the drawer with it.
   *
   * Watching `drawerOpen` for falsiness alone is not enough: on the render that
   * opens the drawer the store has not been updated yet, so the two effects
   * race and the drawer closes itself the moment it opens. Only a genuine true
   * to false TRANSITION counts.
   */
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !drawerOpen && open) close.current();
    wasOpen.current = drawerOpen;
  }, [drawerOpen, open]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /*
   * Drawn in the body, not inside the screen that holds it. Request of
   * 6 October 2026: on an iPhone the page's scroller is a layer of its own, so a
   * drawer inside it sat under the scrim, dimmed, and the first tap on it landed
   * on the scrim and closed it. In the body it is above the scrim, as its
   * z-index says. React still sees it where it was written.
   */
  return createPortal(
    <aside
      className={`${styles.drawer} ${open ? styles.open : ''}`}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-hidden={!open}
      /*
       * `inert` alongside `aria-hidden`, because the two do different jobs and
       * only having the first is a defect Lighthouse names outright:
       * `aria-hidden` removes the panel from the accessibility tree while its
       * close button and every control inside it stay in the tab order. A
       * keyboard user tabbing through a closed drawer lands on buttons they
       * cannot see. `inert` takes the whole subtree out of focus and pointer
       * handling, which is what "closed" was always supposed to mean.
       */
      inert={!open}
    >
      <div className={styles.head}>
        <h3 id={titleId}>{title}</h3>
        <button className={styles.close} onClick={onClose} aria-label={strings.orders.close}>
          <CloseIcon />
        </button>
      </div>
      <div className={styles.body}>{children}</div>
    </aside>,
    document.body,
  );
}

export { styles as drawerStyles };
